import { useSEO } from '../../utils/seo';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://funtimephotobooths.com';

  useSEO({
    title: 'About Fun Time Photobooths | San Diego Photo Booth Experts',
    description: 'Learn about Fun Time Photobooths, San Diego\'s premier photo booth and overhead 360 video booth rental company. Dedicated to creating unforgettable event experiences since our founding.',
    keywords: 'about Fun Time Photobooths, San Diego photo booth company, photo booth experts, event entertainment San Diego',
    canonical: `${siteUrl}/about`,
    ogTitle: 'About Fun Time Photobooths | San Diego Photo Booth Experts',
    ogDescription: 'San Diego\'s premier photo booth and overhead 360 video booth rental company dedicated to creating unforgettable event experiences.',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Fun Time Photobooths',
      description: 'Learn about Fun Time Photobooths, San Diego\'s premier photo booth and overhead 360 video booth rental company.',
      mainEntity: {
        '@type': 'LocalBusiness',
        name: 'Fun Time Photobooths',
        description: 'Premier photo booth and overhead 360 video booth rentals in San Diego.',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'San Diego',
          addressRegion: 'CA',
          addressCountry: 'US',
        },
        areaServed: {
          '@type': 'City',
          name: 'San Diego',
        },
      },
      url: `${siteUrl}/about`,
    },
  });

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <div className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              About Fun Time Photobooths
            </h1>
            <p className="text-lg text-gray-600">
              San Diego's Premier Photo Booth Experience
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-80 md:h-auto w-full">
                  <img
                    src="https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/0fb95216b40f0a992988668993f010e3.jpeg"
                    alt="Fun Time Photobooths Team"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Story</h2>
                  <p className="text-gray-600 mb-4">
                    Fun Time Photobooths is San Diego's premier provider of overhead 360 video booths and luxury photo booth experiences. We specialize in creating unforgettable moments at weddings, corporate events, and celebrations throughout San Diego County.
                  </p>
                  <p className="text-gray-600 mb-4">
                    What sets us apart is our commitment to excellence and attention to detail. We don't just capture moments, we create experiences that your guests will talk about long after your event ends.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <i className="ri-star-fill text-2xl text-orange-500 mt-1"></i>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Exclusive Technology</h3>
                    <p className="text-gray-600">
                      We offer the exclusive overhead 360 video booth experience in San Diego, featuring state-of-the-art equipment and cinematic slow-motion effects.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <i className="ri-team-fill text-2xl text-orange-500 mt-1"></i>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Professional Team</h3>
                    <p className="text-gray-600">
                      Our experienced attendants ensure smooth operation and help your guests create amazing content throughout your event.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <i className="ri-award-fill text-2xl text-orange-500 mt-1"></i>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Trusted by San Diego</h3>
                    <p className="text-gray-600">
                      We're the preferred choice of weddings, brands, and event planners across San Diego County, from La Jolla to Downtown and beyond.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <i className="ri-settings-3-fill text-2xl text-orange-500 mt-1"></i>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Premium Equipment</h3>
                    <p className="text-gray-600">
                      We use only professional-grade cameras, lighting, and equipment to ensure the highest quality results for your event.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-8 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to Create Unforgettable Memories?</h2>
              <p className="text-xl mb-6">
                Let's make your next event extraordinary with our overhead 360 video booth or signature photo booth.
              </p>
              <a
                href="/contact"
                className="inline-block px-8 py-4 bg-white text-orange-500 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
